import { SMoonDrawHistory, getMoonDrawHistory } from '@/apis/casino/draw';
import { queryExpiredList } from '@/apis/rewards';
import MoneyWithCurrencyIcon from '@/components/biz/MoneyWithCurrencyIcon';
import CopyTextField from '@/components/ui/CopyTextField';
import WrappedButton from '@/components/ui/WrappedButton';
import WrappedModal from '@/components/ui/WrappedModal';
import WrappedTable from '@/components/ui/WrappedTable';
import { TInferSchemaType } from '@/constants/schemas';
import useAnchoredPaginationParams from '@/hooks/requests/useAnchoredPaginationParams';
import queryKeysFactory from '@/reactQueryKeys/keysFactory';
import useTheme from '@/theme/useTheme';
import { formatMoneyByCurrency } from '@/utils';
import { UnpackArray } from '@/utils/type';
import { ArrowDropDown } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import React, { useEffect } from 'react';

interface MoonDrawHistoryModalProps {
  open: boolean;
  onClose: () => void;
}

type Record = UnpackArray<TInferSchemaType<typeof SMoonDrawHistory>['list']>;

const WrappedCopyTextField = ({ text, title }) => {
  return (
    <Stack>
      <Typography color={'text.secondary'}>{title}</Typography>
      <CopyTextField text={text}>
        <Typography color={'text.secondary'}>{text}</Typography>
      </CopyTextField>
    </Stack>
  );
};

const MoonDrawHistoryModal: React.FC<MoonDrawHistoryModalProps> = ({
  open,
  onClose,
}) => {
  const [t] = useTranslation();

  const { theme } = useTheme();
  //   const [isLoading, setIsLoading] = React.useState(false);

  const [usedData, setUsedData] = React.useState<any>(undefined);

  const [viewing, setViewing] = React.useState<Record>(undefined);

  const { params, onClickPrev, onClickNext } =
    useAnchoredPaginationParams(usedData);

  //   console.log('%c~LOG~', 'color: yellow; font-size: 16px;', params, usedData);

  const { isLoading } = useQuery({
    queryKey: ['game-record', 'draw', JSON.stringify(params)],
    queryFn: () =>
      getMoonDrawHistory(params).then((res) => {
        setUsedData(res.data);
        return res.data;
      }),
    enabled: open,
  });

  return (
    <>
      <WrappedModal
        open={open}
        onClose={onClose}
        title={t('moon-draw::History')}
      >
        <WrappedTable<Record>
          columns={[
            {
              dataKey: 'createdTime',
              title: t('Date'),
              align: 'left',
              render: (index, value, row) => new Date(value).toLocaleString(),
            },
            {
              dataKey: 'payout',
              title: t('Prize'),
              align: 'center',
              render: (index, value, row) => (
                <MoneyWithCurrencyIcon value={value} />
              ),
            },
            {
              title: t('Details'),
              align: 'center',
              render: (index, value, row) => (
                <WrappedButton
                  variant="plain"
                  onClick={() => {
                    setViewing(row);
                  }}
                >
                  {t('View')}
                </WrappedButton>
              ),
            },
          ]}
          data={usedData?.list || []}
          loading={isLoading}
          anchoredButtonsConfig={{
            onClickPrev,
            onClickNext,
          }}
        />
      </WrappedModal>
      <WrappedModal
        open={!!viewing}
        onClose={() => setViewing(undefined)}
        title={t('moon-draw::Bet Info')}
      >
        {viewing ? (
          <Stack gap={1}>
            <Stack
              sx={{
                border: (theme) => theme.palette.border.normal,
                borderRadius: (theme) => theme.spacing(1),
              }}
            >
              <Stack
                alignItems={'center'}
                padding={1}
                borderBottom={theme.palette.border.normal}
              >
                <Typography variant="h4">{t('Profit')}</Typography>
                <Typography
                  variant="h6"
                  color={
                    Number(viewing.payout) - Number(viewing.amount) >= 0
                      ? 'success.main'
                      : 'error.main'
                  }
                >
                  <MoneyWithCurrencyIcon
                    value={Number(viewing.payout) - Number(viewing.amount)}
                  />
                </Typography>
              </Stack>
              <Stack direction={'row'} justifyContent={'space-around'}>
                <Stack
                  textAlign={'center'}
                  width={'50%'}
                  paddingY={1}
                  borderRight={theme.palette.border.normal}
                >
                  <Typography>{t('Bet Amount')}</Typography>
                  <Typography>
                    <MoneyWithCurrencyIcon value={viewing.amount} />
                  </Typography>
                </Stack>
                <Stack textAlign={'center'} width={'50%'} paddingY={1}>
                  <Typography>{t('Payout')}</Typography>
                  <Typography>{viewing.multiplier}</Typography>
                </Stack>
              </Stack>
            </Stack>
            <Typography>
              {t('moon-draw::Draw Game on')}:{' '}
              {new Date(viewing.createdTime).toLocaleString()}
            </Typography>
            <Typography display={'flex'} gap={1} alignItems={'center'}>
              {t('moon-draw::Bet Id')}: {viewing.id}
              <CopyTextField iconOnly={true} text={viewing.id} />
            </Typography>
            <Divider></Divider>
            <Accordion>
              <AccordionSummary
                expandIcon={<ArrowDropDown />}
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{ paddingLeft: 0, paddingRight: 0 }}
              >
                <Typography color={'text.secondary'}>
                  {t('moon-draw::Game Details')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  paddingRight: 0,
                  paddingLeft: 0,
                }}
              >
                <Stack gap={1}>
                  <WrappedCopyTextField
                    text={viewing.result}
                    title={t('Result')}
                  />
                  <WrappedCopyTextField text={viewing.hash} title={t('Hash')} />
                  <WrappedCopyTextField
                    text={viewing.clientSeed}
                    title={t('Seed')}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
            <WrappedButton>
              <Link href={'/casino/fairness'}>
                {t('What is Provable Fairness?')}
              </Link>
            </WrappedButton>
          </Stack>
        ) : null}
      </WrappedModal>
    </>
  );
};

export default MoonDrawHistoryModal;
